import React from 'react';
import Box from '@mui/material/Box/Box';
import Stack from '@mui/material/Stack/Stack';
import Chip from '@mui/material/Chip/Chip';
import Typography from '@mui/material/Typography/Typography';
import { alpha, getContrastRatio, useTheme } from '@mui/material/styles';
import { HackathonStatus } from '../../utils/hackathonStatus';

export interface HeroStat {
    label: string;
    value: React.ReactNode;
}

/**
 * Banner at the top of a hackathon. Uses the uploaded banner image if branded, otherwise a gradient
 * built from the brand colors.
 */
export const HackathonHero = ({
    name,
    organizationName,
    status,
    stats,
    actions,
    compact
}: {
    name: string,
    organizationName?: string,
    status?: HackathonStatus,
    stats?: HeroStat[],
    actions?: React.ReactNode,
    compact?: boolean
}) => {
    const theme = useTheme();
    const { bannerUrl, logoUrl, gradient, rawPrimary } = theme.brand;

    // keep white text readable on light brand colors
    const overlay = bannerUrl ? 0.5 : (getContrastRatio('#ffffff', rawPrimary) < 3 ? 0.35 : 0.08);
    const background = bannerUrl
        ? `linear-gradient(180deg, rgba(0,0,0,${overlay - 0.2}) 0%, rgba(0,0,0,${overlay}) 100%), url("${bannerUrl.replace(/"/g, '%22')}") center / cover no-repeat`
        : `linear-gradient(rgba(0,0,0,${overlay}), rgba(0,0,0,${overlay})), ${gradient}`;

    return (
        <Box sx={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: '20px',
            color: '#fff',
            background,
            px: { xs: 3, md: 5 },
            py: compact ? { xs: 3, md: 4 } : { xs: 4, md: 6 },
            minHeight: compact ? undefined : { md: 260 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            gap: 3
        }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems={{ xs: 'flex-start', md: 'flex-end' }} justifyContent="space-between">
                <Stack spacing={1.5} sx={{ minWidth: 0 }}>
                    {
                        (logoUrl || organizationName) && (
                            <Stack direction="row" spacing={1.5} alignItems="center">
                                {
                                    logoUrl && (
                                        <Box sx={{
                                            backgroundColor: '#fff',
                                            borderRadius: '10px',
                                            p: 0.75,
                                            display: 'flex',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                                        }}>
                                            <Box component="img" src={logoUrl} alt="" sx={{ height: 32, maxWidth: 120, objectFit: 'contain' }} />
                                        </Box>
                                    )
                                }
                                {
                                    organizationName && <Typography variant="overline" sx={{ opacity: 0.9, lineHeight: 1.4 }}>{organizationName}</Typography>
                                }
                            </Stack>
                        )
                    }
                    <Typography variant="h1" component="h1" sx={{
                        color: '#fff',
                        fontSize: { xs: '2rem', md: compact ? '2.25rem' : '3rem' },
                        textShadow: '0 1px 12px rgba(0,0,0,0.2)',
                        overflowWrap: 'anywhere'
                    }}>{name}</Typography>
                    {
                        status && (
                            <Box>
                                <Chip size="small" label={status.label} sx={{
                                    backgroundColor: alpha('#ffffff', 0.92),
                                    color: theme.palette.text.primary,
                                    fontWeight: 600,
                                    '&::before': {
                                        content: '""',
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        ml: 1.25,
                                        backgroundColor: status.color === 'default' ? theme.palette.grey[500] : theme.palette[status.color].main
                                    }
                                }} />
                            </Box>
                        )
                    }
                </Stack>
                {
                    actions && (
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ flexShrink: 0 }}>
                            {actions}
                        </Stack>
                    )
                }
            </Stack>
            {
                stats && stats.length > 0 && (
                    <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1.5}>
                        {
                            stats.map(stat => (
                                <Box key={stat.label} sx={{
                                    backgroundColor: alpha('#ffffff', 0.14),
                                    border: `1px solid ${alpha('#ffffff', 0.2)}`,
                                    backdropFilter: 'blur(6px)',
                                    borderRadius: '12px',
                                    px: 2,
                                    py: 1.25,
                                    minWidth: 120
                                }}>
                                    <Typography variant="caption" sx={{ opacity: 0.85, display: 'block' }}>{stat.label}</Typography>
                                    <Typography variant="subtitle1" sx={{ lineHeight: 1.3 }}>{stat.value}</Typography>
                                </Box>
                            ))
                        }
                    </Stack>
                )
            }
        </Box>
    )
}

/**
 * Button styling for actions placed on top of the hero banner.
 */
export const heroButtonSx = {
    backgroundColor: 'rgba(255,255,255,0.95)',
    color: 'text.primary',
    '&:hover': {
        backgroundColor: '#fff'
    }
};
